
import { ChitPool } from "@/lib/types";
import { formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";
import { Users, Calendar, DollarSign, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { calculateMonthsCompleted } from "../pages/ChitPoolDetails";

interface ChitPoolCardProps {
  chitPool: ChitPool;
  index: number;
}

const ChitPoolCard = ({ chitPool, index }: ChitPoolCardProps) => {
  const dueDate = new Date(chitPool.firstDueDate);
  dueDate.setMonth(dueDate.getMonth() + chitPool.months);
  const isActive = dueDate > new Date();
  const monthsCompleted = calculateMonthsCompleted(chitPool.firstDueDate);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Link to={`/chit-pools/${chitPool.id}`}>
        <Card className="h-full overflow-hidden card-hover border border-border/50">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <Badge variant={isActive ? "default" : "secondary"} className="mb-2">
                {isActive ? "Active" : "Completed"}
              </Badge>
              <Badge variant="outline" className="font-mono text-xs">
                {monthsCompleted} / {chitPool.months}
              </Badge>
            </div>
            <CardTitle className="line-clamp-1 text-lg font-medium">
              {chitPool.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2 space-y-3">
            <div className="flex items-center text-sm text-muted-foreground gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="font-medium text-foreground">{chitPool.amount.toLocaleString('en-US', { style: 'currency', currency: 'INR' })}</span>
              <span className="mx-1">•</span>
              <span>{chitPool.months} months</span>
            </div>
            
            <div className="flex items-center text-sm text-muted-foreground gap-2">
              <Calendar className="h-4 w-4" />
              <span>First due: {new Date(chitPool.firstDueDate).toLocaleDateString()}</span>
            </div>
            
            <div className="flex items-center text-sm text-muted-foreground gap-2">
              <Users className="h-4 w-4" />
              <span>{chitPool.members.length} members</span>
            </div>
          </CardContent>
          <CardFooter className="pt-2 flex justify-between items-center">
            <div className="text-xs text-muted-foreground">
              Created {formatDistanceToNow(new Date(chitPool.createdAt), { addSuffix: true })}
            </div>
            <ArrowRight className="h-4 w-4 text-primary" />
          </CardFooter>
        </Card>
      </Link>
    </motion.div>
  );
};

export default ChitPoolCard;
